import React, { useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import Tooltip from 'react-tooltip'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import {
  InventoryLogType,
  InventoryLogSubtypeForDB,
  inventoryLogSubtypes,
  IInventoryLog,
} from '@getgreenline/inventory/build/models/inventoryLog'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../../constants/general'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { Select, message } from 'antd-v4'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { InventoryAdditionOptions } from './InventoryAdditionOptions'
import { InventoryReductionOptions } from './InventoryReductionOptions'
import { BlazeAlert } from '../../../../components/BlazeAlert/BlazeAlert'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  onHide: () => void
  onUpdate: () => void
  log: IInventoryLog
}

const _ModifyLogModalOCS = ({
  currentCompanyStore,
  onHide,
  onUpdate,
  log,
}: Props & InjectedProps) => {
  const [newLogType, setNewLogType] = useState<InventoryLogType | undefined>(undefined)
  const [showSubtypes, setShowSubtypes] = useState<boolean>(false)
  const [subtype, setSubtype] = useState<InventoryLogSubtypeForDB | undefined>(undefined)
  const [isApplying, setIsApplying] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const logCreateDate = dayjs(log.createDate).format('MMM DD, h:mma')
  const logUpdateValue = log.updateValue
  const logUpdateValueClassname = logUpdateValue >= 0 ? 'text-success' : 'text-danger'

  const companyId = currentCompanyStore?.company?.id

  const subtypeOptions = Object.keys(inventoryLogSubtypes).map((key) => ({
    value: inventoryLogSubtypes[key].dbVal,
    label: inventoryLogSubtypes[key].blazeVal,
  }))

  const applyType = async () => {
    setIsApplying(true)
    setErrorMessage(undefined)

    try {
      if (!companyId) {
        throw new Error('No company Id')
      }
      if (!newLogType) {
        throw new Error('Type not selected')
      }
      if (newLogType !== InventoryLogType.AUDIT) {
        await InventoryLogAPI.updateInventoryLog(companyId, log.id, newLogType)
        message.success('Inventory Log reason was successfully modified')
        onHide()
        onUpdate()
      } else {
        setShowSubtypes(true)
      }
    } catch (error) {
      console.error(error)
      setErrorMessage(parseErrorMsg(error) || 'Failed to apply')
    } finally {
      setIsApplying(false)
    }
  }

  const applySubtype = async () => {
    setIsApplying(true)
    setErrorMessage(undefined)

    try {
      if (!companyId) {
        throw new Error('No company Id')
      }
      if (!newLogType) {
        throw new Error('Type not selected')
      }
      if (!subtype) {
        throw new Error('Subtype not selected')
      }
      await InventoryLogAPI.updateInventoryLog(companyId, log.id, newLogType, subtype)
      message.success('Audit reason was successfully modified')
      onHide()
      onUpdate()
    } catch (error) {
      console.error(error)
      setErrorMessage(parseErrorMsg(error) || 'Failed to apply')
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} toggle={onHide} size='md'>
        <ModalHeader toggle={onHide}>Modify inventory log</ModalHeader>
        <ModalBody>
          <p>
            On {logCreateDate},
            <br />
            <b>{log.userName}</b> recorded an audit with a discrepancy of{' '}
            {logUpdateValue > 0 && '+'}
            <b className={logUpdateValueClassname + ' mr-1'}>{logUpdateValue}</b>
            for {log.productName}. Please select a valid Health Canada reason below.
          </p>

          <br />

          <p className='text-muted'>
            Why do I have to modify my inventory log reasons?{' '}
            <span
              data-tip
              data-for='logReasonsTip'
              className='fa fa-info-circle ml-2'
              style={{ cursor: 'pointer' }}
            />
          </p>
          <Tooltip place='bottom' id='logReasonsTip' effect='solid'>
            In {BLAZE_COMPANY}, audited products will show up as "other additions" or "other
            reductions" on your compliance reports.
            <br />
            Health Canada would like unaccounted for discrepancies to be reduced as much as
            possible.
            <br />
            <br />
            If your additions and reductions for a product are the same (ex. +50 and -50), then they
            can be safely ignored.
            <br />
            {BLAZE_COMPANY} will automatically cancel them out.
            <br />
            <br />
            Small discrepancies in the single digits are understandable in a retail setting. Double
            digits are not.
          </Tooltip>

          {!showSubtypes && logUpdateValue > 0 && (
            <>
              <InventoryAdditionOptions
                newLogType={newLogType}
                setType={(type: InventoryLogType) => setNewLogType(type)}
              />
              <div className='mt-3'>
                <BlazeButton
                  buttonProps={{
                    className: 'w-100 btn btn-success btn-lg',
                    type: 'primary',
                    size: 'large',
                    disabled: !newLogType || isApplying,
                    onClick: applyType,
                  }}
                  titleProps={{ title: newLogType === InventoryLogType.AUDIT ? 'Next' : 'Modify' }}
                />
              </div>
            </>
          )}

          {!showSubtypes && logUpdateValue < 0 && (
            <>
              <InventoryReductionOptions
                newLogType={newLogType}
                setType={(type: InventoryLogType) => setNewLogType(type)}
              />
              <div className='mt-3'>
                <BlazeButton
                  buttonProps={{
                    className: 'w-100 btn btn-success btn-lg',
                    type: 'primary',
                    size: 'large',
                    disabled: !newLogType || isApplying,
                    onClick: applyType,
                  }}
                  titleProps={{ title: newLogType === InventoryLogType.AUDIT ? 'Next' : 'Modify' }}
                />
              </div>
            </>
          )}

          {showSubtypes && (
            <>
              <p className='mt-0 mb-2' style={{ fontSize: '1.1rem' }}>
                Select the reason of your audit
              </p>
              <Select
                style={{ width: '100%' }}
                className='mb-1'
                placeholder={'Select reason'}
                options={subtypeOptions}
                onChange={(value: InventoryLogSubtypeForDB) => setSubtype(value)}
              />
              <div className='mt-3'>
                <BlazeButton
                  buttonProps={{
                    className: 'w-100 btn btn-success btn-lg mt-2',
                    type: 'primary',
                    size: 'large',
                    disabled: !subtype || isApplying,
                    onClick: applySubtype,
                  }}
                  titleProps={{ title: 'Modify' }}
                />
              </div>
            </>
          )}

          {errorMessage && (
            <BlazeAlert type='error' showIcon style={{ marginTop: 5 }}>
              {errorMessage}
            </BlazeAlert>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export const ModifyLogModalOCS = inject('currentCompanyStore')(observer(_ModifyLogModalOCS))
